import React, { useState, useEffect } from 'react';
import './MedicalTravellerAdmin.css';
import { CSVLink } from 'react-csv';
import moment from 'moment-timezone';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';

const API_SERVER = process.env.REACT_APP_AI_FORM_SERVER || 'http://localhost:4454';
const SURVEY_ID = '68c0a1b2ece92761170b9601';

const CHART_COLORS = [
    '#096D6F',
    '#00A4A1',
    '#0088FE',
    '#00C49F',
    '#FFBB28',
    '#FF8042',
    '#8884d8',
    '#82ca9d',
    '#d0ed57',
    '#a4de6c',
    '#ffc658',
    '#8dd1e1',
];

const EXCLUDED_COLUMNS = new Set([
    '_id',
    'survey_id',
    'clientInfo_ip',
    'clientInfo_screen',
    'clientInfo_colorDepth',
    'clientInfo_cookieEnabled',
    'clientInfo_hardwareConcurrency',
    'clientInfo_languages',
    'clientInfo_timezoneOffset',
    'clientInfo_touchPoints',
    'clientInfo_vendor',
    'clientInfo_viewport',
    'clientInfo_recordedAt',
]);

const getQuestionGroupInfo = (header) => {
    const label = header.replace(/^response_/, '');
    const match = label.match(/^Q(\d+)([a-z])?\.\s*(.*)$/i);
    if (match) {
        const letter = match[2] ? match[2].toLowerCase() : '';
        return {
            groupKey: `Q${match[1]}${letter}`,
            num: parseInt(match[1], 10),
            letter,
            text: match[3].trim(),
            fullLabel: label,
        };
    }
    // Questions without Q# (e.g. hospital selection) group as Q0
    if (header.startsWith('response_')) {
        return {
            groupKey: 'Q0',
            num: 0,
            letter: '',
            text: label,
            fullLabel: label,
        };
    }
    return null;
};

const looksIndonesian = (text) =>
    /\b(Anda|yang|Jika|Lainnya|sebutkan|berdomisili|rumah sakit|kunjungan|perjalanan|Sedang|Berapa|Bagaimana|Apa |Di mana|Pilih semua)\b/i.test(
        text
    );

const buildBilingualLabel = (groupKey, variants) => {
    const texts = variants.map((v) => v.text).filter(Boolean);
    if (texts.length === 0) return groupKey;

    const en = variants.find((v) => !looksIndonesian(v.text));
    const id = variants.find((v) => looksIndonesian(v.text));

    if (en && id && en.text !== id.text) {
        return `${groupKey}. ${en.text} / ${id.text}`;
    }
    if (en) return `${groupKey}. ${en.text}`;
    if (id) return `${groupKey}. ${id.text}`;
    return `${groupKey}. ${[...new Set(texts)].join(' / ')}`;
};

const compareGroupKeys = (a, b) => {
    const parse = (key) => {
        const match = key.match(/^Q(\d+)([a-z])?$/i);
        if (!match) return { num: Number.MAX_SAFE_INTEGER, letter: '', key };
        return {
            num: parseInt(match[1], 10),
            letter: match[2] ? match[2].toLowerCase() : '',
            key,
        };
    };
    const ka = parse(a);
    const kb = parse(b);
    if (ka.num !== kb.num) return ka.num - kb.num;
    if (ka.letter !== kb.letter) return ka.letter.localeCompare(kb.letter);
    return ka.key.localeCompare(kb.key);
};

const getGroupKeyFromQuestion = (question) => {
    const enLabel = question.questions?.en || '';
    const idLabel = question.questions?.id || '';
    const info =
        getQuestionGroupInfo(`response_${enLabel}`) ||
        getQuestionGroupInfo(`response_${idLabel}`);
    return info?.groupKey || null;
};

/** Build checkbox answer maps + question type meta from survey details. */
const buildQuestionCatalog = (details) => {
    const answerMaps = {};
    const questionMeta = {};

    (details || []).forEach((question) => {
        const groupKey = getGroupKeyFromQuestion(question);
        if (!groupKey) return;

        questionMeta[groupKey] = {
            qtype: question.qtype || 'text',
            optionCount: (question.options || []).length,
        };

        if (
            question.qtype !== 'checkbox_single' &&
            question.qtype !== 'checkbox_multiple'
        ) {
            return;
        }

        const answerMap = new Map();
        (question.options || []).forEach((opt) => {
            const enOpt = String(opt?.en ?? '').trim();
            const idOpt = String(opt?.id ?? '').trim();
            if (!enOpt && !idOpt) return;

            const bilingual =
                enOpt && idOpt && enOpt !== idOpt
                    ? `${enOpt} / ${idOpt}`
                    : enOpt || idOpt;

            if (enOpt) answerMap.set(enOpt, bilingual);
            if (idOpt) answerMap.set(idOpt, bilingual);
        });

        answerMaps[groupKey] = answerMap;
    });

    return { answerMaps, questionMeta };
};

const mapCheckboxAnswerValue = (value, answerMap) => {
    if (!answerMap || value === undefined || value === null) return value ?? '';
    const raw = String(value);
    if (!raw.trim()) return raw;

    return raw
        .split('; ')
        .map((part) => {
            const trimmed = part.trim();
            if (!trimmed) return part;
            return answerMap.get(trimmed) || trimmed;
        })
        .join('; ');
};

const shortenLabel = (label, max = 42) => {
    const text = String(label || '');
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1)}…`;
};

const pickChartType = (qtype, categoryCount, optionCount) => {
    if (qtype === 'text') return 'list';
    if (qtype === 'checkbox_multiple' || optionCount > 6 || categoryCount > 6) {
        return 'bar';
    }
    return 'doughnut';
};

const buildAgentChartData = (data) => {
    const counts = {};
    (data || []).forEach((entry) => {
        const agentId = String(entry?.agentId ?? '').trim() || '(blank)';
        counts[agentId] = (counts[agentId] || 0) + 1;
    });

    return Object.entries(counts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
};

const buildQuestionCharts = (questionColumns, rows, headers, questionMeta) => {
    return questionColumns.map((col) => {
        const colIndex = headers.indexOf(col.label);
        const meta = questionMeta[col.groupKey] || { qtype: 'text', optionCount: 0 };
        const isCheckbox =
            meta.qtype === 'checkbox_single' || meta.qtype === 'checkbox_multiple';

        const counts = {};
        const textAnswers = [];

        rows.forEach((row) => {
            const raw = colIndex >= 0 ? row[colIndex] : '';
            if (raw === undefined || raw === null || String(raw).trim() === '') return;

            if (isCheckbox) {
                String(raw)
                    .split('; ')
                    .map((p) => p.trim())
                    .filter(Boolean)
                    .forEach((part) => {
                        counts[part] = (counts[part] || 0) + 1;
                    });
            } else {
                textAnswers.push(String(raw).trim());
            }
        });

        if (isCheckbox) {
            const data = Object.entries(counts)
                .map(([name, value]) => ({ name, value }))
                .sort((a, b) => b.value - a.value);

            return {
                groupKey: col.groupKey,
                title: col.label,
                chartType: pickChartType(meta.qtype, data.length, meta.optionCount),
                data,
                total: data.reduce((sum, d) => sum + d.value, 0),
            };
        }

        // Free text: group identical answers, keep list of unique entries with counts
        const textCounts = {};
        textAnswers.forEach((ans) => {
            textCounts[ans] = (textCounts[ans] || 0) + 1;
        });
        const listItems = Object.entries(textCounts)
            .map(([text, count]) => ({ text, count }))
            .sort((a, b) => b.count - a.count || a.text.localeCompare(b.text));

        return {
            groupKey: col.groupKey,
            title: col.label,
            chartType: 'list',
            data: listItems,
            total: textAnswers.length,
        };
    });
};

function MedicalTravellerAdmin() {
    const [responses, setResponses] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [tableHeaders, setTableHeaders] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [questionCharts, setQuestionCharts] = useState([]);
    const [agentChartData, setAgentChartData] = useState([]);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const [responsesResult, questionsResult] = await Promise.all([
                    fetch(
                        `${API_SERVER}/api/getMedicalTravellerResponses?_=${new Date().getTime()}`
                    ).then((res) => res.json()),
                    fetch(`${API_SERVER}/api/getQuestion?_=${new Date().getTime()}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ survey_id: SURVEY_ID }),
                    }).then((res) => res.json()),
                ]);

                const details = Array.isArray(questionsResult)
                    ? questionsResult[0]?.details || []
                    : [];
                const { answerMaps, questionMeta } = buildQuestionCatalog(details);

                if (responsesResult.success) {
                    setResponses(responsesResult.data || []);
                    processData(responsesResult.data || [], answerMaps, questionMeta);
                } else {
                    alert('Failed to fetch responses');
                }
            } catch (error) {
                console.log('Error loading Medical Traveller admin data:', error);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    const flattenEntry = (entry) => {
        const flat = {
            language: entry.language,
            agentId: entry.agentId || '',
            submissionDate: entry.submissionDate || '',
        };

        if (entry.clientInfo && typeof entry.clientInfo === 'object') {
            Object.keys(entry.clientInfo).forEach((key) => {
                const columnKey = `clientInfo_${key}`;
                if (EXCLUDED_COLUMNS.has(columnKey)) return;

                const value = entry.clientInfo[key];
                flat[columnKey] =
                    value !== null && typeof value === 'object'
                        ? JSON.stringify(value)
                        : value ?? '';
            });
        }

        if (entry.response && typeof entry.response === 'object') {
            Object.keys(entry.response).forEach((key) => {
                const value = entry.response[key];
                flat[`response_${key}`] = Array.isArray(value)
                    ? value.join('; ')
                    : value ?? '';
            });
        }

        return flat;
    };

    const processData = (data, answerMaps = {}, questionMeta = {}) => {
        if (!data || data.length === 0) {
            setCsvData([]);
            setTableHeaders([]);
            setTableRows([]);
            setQuestionCharts([]);
            setAgentChartData([]);
            return;
        }

        const flattened = data.map(flattenEntry);
        const headerSet = new Set();
        flattened.forEach((row) => {
            Object.keys(row).forEach((key) => {
                if (!EXCLUDED_COLUMNS.has(key)) headerSet.add(key);
            });
        });

        const preferredOrder = [
            'language',
            'agentId',
            'submissionDate',
            'clientInfo_deviceId',
            'clientInfo_userAgent',
            'clientInfo_platform',
            'clientInfo_timezone',
            'clientInfo_language',
        ];

        const questionGroups = new Map();
        const otherHeaders = [];

        [...headerSet].forEach((key) => {
            if (preferredOrder.includes(key)) return;

            const info = getQuestionGroupInfo(key);
            if (info) {
                if (!questionGroups.has(info.groupKey)) {
                    questionGroups.set(info.groupKey, []);
                }
                questionGroups.get(info.groupKey).push({ sourceKey: key, ...info });
                return;
            }
            otherHeaders.push(key);
        });

        const sortedGroupKeys = [...questionGroups.keys()].sort(compareGroupKeys);
        const questionColumns = sortedGroupKeys.map((groupKey) => {
            const variants = questionGroups.get(groupKey);
            return {
                label: buildBilingualLabel(groupKey, variants),
                sources: variants.map((v) => v.sourceKey),
                groupKey,
            };
        });

        const metaHeaders = preferredOrder.filter((key) => headerSet.has(key));
        const columns = [
            ...metaHeaders.map((key) => ({ label: key, sources: [key] })),
            ...questionColumns,
            ...otherHeaders
                .sort((a, b) => a.localeCompare(b))
                .map((key) => ({ label: key, sources: [key] })),
        ];

        const headers = columns.map((col) => col.label);
        const rows = flattened.map((row) =>
            columns.map((col) => {
                let value = '';
                for (const source of col.sources) {
                    const candidate = row[source];
                    if (
                        candidate !== undefined &&
                        candidate !== null &&
                        String(candidate).trim() !== ''
                    ) {
                        value = candidate;
                        break;
                    }
                }

                if (col.groupKey && answerMaps[col.groupKey]) {
                    return mapCheckboxAnswerValue(value, answerMaps[col.groupKey]);
                }
                return value;
            })
        );

        setTableHeaders(headers);
        setTableRows(rows);
        setCsvData([headers, ...rows]);
        setAgentChartData(buildAgentChartData(data));
        setQuestionCharts(buildQuestionCharts(questionColumns, rows, headers, questionMeta));
    };

    const handlePasscode = () => {
        const password = prompt('Enter password to view and download data:');
        if (password === 'Island@308') {
            setIsAuthorized(true);
        } else {
            alert('Incorrect password!');
        }
    };

    const formatHeader = (header) =>
        header.replace(/^clientInfo_/, 'Client: ');

    const renderChartCard = (chart) => {
        if (chart.chartType === 'list') {
            return (
                <div key={chart.groupKey} className="mt-chart-card mt-chart-card--list">
                    <h3 className="mt-chart-title" title={chart.title}>
                        {chart.title}
                    </h3>
                    <p className="mt-chart-meta">{chart.total} free-text answer(s)</p>
                    {chart.data.length === 0 ? (
                        <p className="mt-chart-empty">No answers yet.</p>
                    ) : (
                        <ul className="mt-chart-list">
                            {chart.data.map((item, idx) => (
                                <li key={`${chart.groupKey}-${idx}`}>
                                    <span className="mt-chart-list-text">{item.text}</span>
                                    {item.count > 1 && (
                                        <span className="mt-chart-list-count">×{item.count}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            );
        }

        if (chart.data.length === 0) {
            return (
                <div key={chart.groupKey} className="mt-chart-card">
                    <h3 className="mt-chart-title" title={chart.title}>
                        {chart.title}
                    </h3>
                    <p className="mt-chart-empty">No answers yet.</p>
                </div>
            );
        }

        if (chart.chartType === 'bar') {
            return (
                <div key={chart.groupKey} className="mt-chart-card">
                    <h3 className="mt-chart-title" title={chart.title}>
                        {chart.title}
                    </h3>
                    <p className="mt-chart-meta">{chart.total} selection(s)</p>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                            data={chart.data}
                            layout="vertical"
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={140}
                                tickFormatter={(v) => shortenLabel(v, 28)}
                                tick={{ fontSize: 11 }}
                            />
                            <Tooltip />
                            <Bar dataKey="value" fill="#096D6F" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        return (
            <div key={chart.groupKey} className="mt-chart-card">
                <h3 className="mt-chart-title" title={chart.title}>
                    {chart.title}
                </h3>
                <p className="mt-chart-meta">{chart.total} selection(s)</p>
                <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                        <Pie
                            data={chart.data}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            nameKey="name"
                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                            {chart.data.map((entry, index) => (
                                <Cell
                                    key={`${chart.groupKey}-cell-${index}`}
                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            formatter={(value) => shortenLabel(value, 36)}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div className={`mt-admin-page ${isAuthorized ? 'mt-admin-page--data' : ''}`}>
            <div className="mt-admin-header">
                <h1>Medical Traveller Survey Admin</h1>
                <p className="mt-admin-subtitle">
                    {loading ? 'Loading...' : `Total responses: ${responses.length}`}
                </p>

                {!isAuthorized ? (
                    <button type="button" onClick={handlePasscode} className="mt-admin-download-btn">
                        Key In Passcode
                    </button>
                ) : (
                    <div className="mt-admin-actions">
                        <CSVLink
                            data={csvData}
                            filename={`MedicalTravellerResponse_${moment()
                                .tz('Asia/Kuala_Lumpur')
                                .format('DD_MM_YYYY_HH_mm_ss')}.csv`}
                            id="mtCsvDownloadLink"
                        >
                            <button type="button" className="mt-admin-download-btn">
                                Download CSV
                            </button>
                        </CSVLink>
                    </div>
                )}
            </div>

            {isAuthorized && (
                <>
                    <div className="mt-admin-table-wrap">
                        {tableRows.length === 0 ? (
                            <p className="mt-admin-empty">No responses found.</p>
                        ) : (
                            <table className="mt-admin-table">
                                <thead>
                                    <tr>
                                        <th className="mt-admin-no">No.</th>
                                        {tableHeaders.map((header) => (
                                            <th key={header} title={header}>
                                                {formatHeader(header)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableRows.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            <td className="mt-admin-no">{rowIndex + 1}.</td>
                                            {row.map((cell, cellIndex) => (
                                                <td
                                                    key={`${rowIndex}-${cellIndex}`}
                                                    title={String(cell)}
                                                >
                                                    {String(cell)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {(agentChartData.length > 0 || questionCharts.length > 0) && (
                        <section className="mt-charts-section">
                            {agentChartData.length > 0 && (
                                <div className="mt-chart-card mt-chart-card--agent">
                                    <h2 className="mt-charts-heading">Responses by Agent</h2>
                                    <p className="mt-chart-meta">
                                        {agentChartData.reduce((sum, d) => sum + d.value, 0)}{' '}
                                        response(s) across {agentChartData.length} agent(s)
                                    </p>
                                    <ResponsiveContainer
                                        width="100%"
                                        height={Math.max(260, agentChartData.length * 36)}
                                    >
                                        <BarChart
                                            data={agentChartData}
                                            layout="vertical"
                                            margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" allowDecimals={false} />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                width={160}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <Tooltip />
                                            <Bar
                                                dataKey="value"
                                                name="Responses"
                                                fill="#00A4A1"
                                                radius={[0, 4, 4, 0]}
                                                label={{ position: 'right', fontSize: 12 }}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {questionCharts.length > 0 && (
                                <>
                                    <h2 className="mt-charts-heading">Question Charts</h2>
                                    <div className="mt-charts-grid">
                                        {questionCharts.map((chart) => renderChartCard(chart))}
                                    </div>
                                </>
                            )}
                        </section>
                    )}
                </>
            )}
        </div>
    );
}

export default MedicalTravellerAdmin;
